import { create } from 'zustand';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'dydation:theme';

/**
 * Rozwiązanie preferencji do konkretnego motywu.
 *
 * `system` nie jest trzecim motywem — to zgoda na to, żeby decydował system.
 * Trzymamy tę różnicę, bo użytkownik, który wybrał „jasny", ma zostać przy
 * jasnym także wtedy, gdy jego system przełączy się wieczorem na ciemny.
 */
function resolve(preference: ThemePreference): ResolvedTheme {
  if (preference !== 'system') return preference;

  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function readStored(): ThemePreference {
  const stored = globalThis.localStorage?.getItem(STORAGE_KEY);

  return stored === 'light' || stored === 'dark' || stored === 'system'
    ? stored
    : 'system';
}

function apply(resolved: ResolvedTheme): void {
  document.documentElement.dataset['theme'] = resolved;
}

interface ThemeState {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
  /** Skrót pod przełącznik: jasny ↔ ciemny, zawsze wychodząc z tego, co widać. */
  toggle: () => void;
}

const initialPreference = readStored();

export const useThemeStore = create<ThemeState>((set, get) => ({
  preference: initialPreference,
  resolved: resolve(initialPreference),

  setPreference: (preference) => {
    const resolved = resolve(preference);

    globalThis.localStorage?.setItem(STORAGE_KEY, preference);
    apply(resolved);
    set({ preference, resolved });
  },

  toggle: () => {
    get().setPreference(get().resolved === 'dark' ? 'light' : 'dark');
  },
}));

/**
 * Nasłuch zmiany ustawienia systemowego — dotyczy WYŁĄCZNIE preferencji
 * `system`. Bez tego użytkownik z ustawieniem automatycznym musiałby odświeżyć
 * stronę, żeby aplikacja nadążyła za zmierzchem.
 */
export function watchSystemTheme(): () => void {
  const query = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
  if (!query) return () => {};

  const onChange = (): void => {
    const { preference } = useThemeStore.getState();
    if (preference !== 'system') return;

    const resolved = resolve('system');
    apply(resolved);
    useThemeStore.setState({ resolved });
  };

  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}
